import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, withModifiers as _withModifiers, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex justify-flex-start items-center" }
const _hoisted_2 = { class: "mr-4 flex items-center justify-center h-12 w-12 rounded-full bg-primary-color-100" }
const _hoisted_3 = { class: "grid grid-cols-2 gap-4" }
const _hoisted_4 = { class: "grid grid-cols-2 gap-4" }
const _hoisted_5 = { class: "flex justify-end" }

import { computed, ref, watch, nextTick } from "vue"
import { useRoute } from "vue-router"
import { DialogTitle } from "@headlessui/vue"
import GgmsButton from "@/components/GgmsButton.vue"
import GgmsModal from "@/components/GgmsModal.vue"
import GgmsInput from "@/components/GgmsInput.vue"
import GgmsPhoneTelInput from "@/components/GgmsPhoneTelInput.vue"
import GgmsDropdown from "@/components/GgmsDropdown.vue"
import GgmsMultiSelect from "@/components/GgmsMultiSelect.vue"
import { UserAddIcon } from "@heroicons/vue/outline"
import { useLeadsStore } from "@/stores/leads"
import { useGridCollectionStore } from "@/stores/grid-collection"
import { useTagStore } from "@/stores/tag"
import { useAgencyStore } from "@/stores/agency"
import { useOriginStore } from "@/stores/origin"
import { ToastService } from "@/shared/services/toast"
import { Phone } from "@/shared/models/phone"
import { isInvalid, hasErrorMessage, getDomainId } from "@/shared/utils/helpers"
import { isValidPhoneNumber } from "libphonenumber-js"
import { Lead, CreateLeadResponse } from "@/shared/models/lead"
import { GgmsError } from "@/shared/services/client"
import { Tag } from "@/shared/models/tag"

interface Props {
    open: boolean
    lead?: Partial<Lead>
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AddLeadModal',
  props: {
    open: { type: Boolean },
    lead: {}
  },
  emits: ["closeModal"],
  setup(__props: any, { emit: __emit }) {

const originStore = useOriginStore()
const leadsStore = useLeadsStore()
const gridCollectionStore = useGridCollectionStore()
const tagStore = useTagStore()
const agencyStore = useAgencyStore()
const toastService = new ToastService()
const route = useRoute()

const props = __props

const emit = __emit

const collections = computed(() => gridCollectionStore.collectionsResponse)

const addPersonModalOpen = ref(false)
const firstName = ref()
const lastName = ref()
const email = ref()
const phone = ref({ country: "RO", number: "" } as Phone)
const origin = ref([])
const tag = ref([])

const error = ref()
const errorField = ref("")
const isLoading = ref(false)
const origins = computed(() => originStore.tableState.data || [])
const tags = ref<Tag[]>([])

const isPhoneInputReadOnly = computed(() => !!props.lead?.phone?.number)

const selectedDomain = ref(initializeDomain())
const domainOptions = computed(() => agencyStore.agency.agencyDomains)

async function addLead() {
    try {
        isLoading.value = true
        error.value = undefined

        if (phone.value?.number && !isValidPhoneNumber(phone.value?.number, phone.value.country)) {
            error.value = {
                field: "phone.number",
                message: "Invalid phone number",
            }
            isLoading.value = false
            return
        }

        if (!selectedDomain.value) {
            nextTick(() => {
                error.value = {
                    field: "domain",
                    message: "Domain is required. Please select a domain.",
                }
            })
            isLoading.value = false
            return
        }
        localStorage.setItem("leadDomainId", selectedDomain.value)

        const payload = {
            firstName: firstName.value?.trim() || undefined,
            lastName: lastName.value?.trim() || undefined,
            email: email.value ? { emailAddress: email.value?.trim() } : undefined,
            phone: phone.value?.number
                ? ({
                      country: phone.value.country,
                      number: phone.value.number,
                  } as Phone)
                : undefined,
            origins: origin.value?.length ? origin.value : undefined,
            tags: tag.value?.length ? tag.value : undefined,
        }

        const response = props.lead?._id
            ? await leadsStore.updateLead(props.lead._id, payload)
            : await leadsStore.addLead(payload)

        localStorage.removeItem("leadDomainId")

        if (!response) {
            return
        }

        toastService.addToast({
            type: "success",
            message: (response as CreateLeadResponse)?.updatedExisting
                ? "There was already a lead that was updated with the new details."
                : "A new person was created.",
        })

        addPersonModalOpen.value = false
        isLoading.value = false

        gridCollectionStore.collectionType = "lead"
        await gridCollectionStore.getCollections()

        if (route.name === "people") {
            if (gridCollectionStore.selectedTabId) {
                leadsStore.tableState.ids = collections.value
                    .find(
                        (collectionResponse) => collectionResponse.collection._id === gridCollectionStore.selectedTabId
                    )
                    ?.ids.join(",")
            }

            await leadsStore.loadLeads(!!leadsStore.tableState.filtersGroups?.length, true)
        }

        closeModal()
    } catch (e) {
        nextTick(() => {
            const err = e as GgmsError
            error.value = err
            if (["email", "email.emailaddress"].includes(err.field as string)) {
                errorField.value = err.field as string
            }
        })
        localStorage.removeItem("leadDomainId")
    } finally {
        isLoading.value = false
    }
}

function closeModal() {
    emit("closeModal")
    firstName.value = undefined
    lastName.value = undefined
    email.value = undefined
    phone.value = { country: "RO", number: "" }
    origin.value = []
    tag.value = []
    error.value = undefined
    selectedDomain.value = initializeDomain()
}

function initializeDomain() {
    const domain = getDomainId()
    return domain === "all" ? null : domain
}

watch(
    () => agencyStore.selectedDomain,
    (newValue) => {
        selectedDomain.value = newValue._id === "all" ? "" : newValue._id
    }
)

watch(
    () => props.open,
    async (value) => {
        if (value) {
            tags.value = await tagStore.getTagsForLeads()
        }

        if (!value || !props.lead) {
            return
        }
        phone.value.number = props.lead?.phone?.number || ""
        phone.value.country = props.lead?.phone?.country || "RO"
    }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(GgmsModal, {
    open: props.open,
    onCloseModal: closeModal
  }, {
    header: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_unref(UserAddIcon), {
            class: "h-6 w-6 text-primary-color-600",
            "aria-hidden": "true"
          })
        ]),
        _createVNode(_unref(DialogTitle), {
          as: "h3",
          class: "text-lg leading-6 font-medium text-gray-900"
        }, {
          default: _withCtx(() => [
            _createTextVNode("Add Person")
          ]),
          _: 1
        })
      ])
    ]),
    under: _withCtx(() => [
      _createElementVNode("div", _hoisted_5, [
        _createVNode(GgmsButton, {
          styleType: "secondary",
          onClick: closeModal
        }, {
          default: _withCtx(() => [
            _createTextVNode("Cancel")
          ]),
          _: 1
        }),
        _createVNode(GgmsButton, {
          class: "ml-3",
          disabled: isLoading.value,
          onClick: addLead
        }, {
          default: _withCtx(() => [
            _createTextVNode("Add person")
          ]),
          _: 1
        }, 8, ["disabled"])
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("form", {
        class: "flex flex-col gap-y-[30px]",
        onSubmit: _withModifiers(addLead, ["prevent"])
      }, [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(GgmsInput, {
            modelValue: firstName.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((firstName).value = $event)),
            name: "first-name",
            label: "First name",
            invalid: _unref(isInvalid)('firstname', error.value),
            errorMessage: _unref(hasErrorMessage)('firstname', error.value)
          }, null, 8, ["modelValue", "invalid", "errorMessage"]),
          _createVNode(GgmsInput, {
            modelValue: lastName.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((lastName).value = $event)),
            name: "last-name",
            label: "Last name",
            invalid: _unref(isInvalid)('lastname', error.value),
            errorMessage: _unref(hasErrorMessage)('lastname', error.value)
          }, null, 8, ["modelValue", "invalid", "errorMessage"])
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(GgmsInput, {
            modelValue: email.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((email).value = $event)),
            type: "email",
            name: "email",
            label: "Email address",
            invalid: _unref(isInvalid)(errorField.value, error.value),
            errorMessage: _unref(hasErrorMessage)(errorField.value, error.value)
          }, null, 8, ["modelValue", "invalid", "errorMessage"]),
          _createVNode(GgmsPhoneTelInput, {
            modelValue: phone.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((phone).value = $event)),
            name: "phone",
            label: "Cell phone",
            placeholder: "Enter phone number",
            readonly: isPhoneInputReadOnly.value,
            invalid: _unref(isInvalid)('phone.number', error.value),
            errorMessage: _unref(hasErrorMessage)('phone.number', error.value)
          }, null, 8, ["modelValue", "readonly", "invalid", "errorMessage"])
        ]),
        _createVNode(GgmsDropdown, {
          modelValue: selectedDomain.value,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((selectedDomain).value = $event)),
          options: domainOptions.value,
          name: "domain",
          label: "Domain",
          "is-show-clear": true,
          optionLabel: "url",
          optionValue: "_id",
          placeholder: "Select Domain",
          class: "w-full",
          invalid: _unref(isInvalid)('domain', error.value),
          "error-message": _unref(hasErrorMessage)('domain', error.value)
        }, null, 8, ["modelValue", "options", "invalid", "error-message"]),
        _createVNode(GgmsMultiSelect, {
          modelValue: origin.value,
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((origin).value = $event)),
          label: "Origins",
          placeholder: "Select a lead origin",
          display: "chip",
          options: origins.value,
          optionLabel: "displayName",
          classes: "w-full"
        }, null, 8, ["modelValue", "options"]),
        _createVNode(GgmsMultiSelect, {
          modelValue: tag.value,
          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((tag).value = $event)),
          label: "Tags",
          placeholder: "+ Add Tag",
          display: "chip",
          options: tags.value,
          optionLabel: "displayName",
          invalid: _unref(isInvalid)('tags', error.value),
          errorMessage: _unref(hasErrorMessage)('tags', error.value),
          classes: "w-full"
        }, null, 8, ["modelValue", "options", "invalid", "errorMessage"])
      ], 32)
    ]),
    _: 1
  }, 8, ["open"]))
}
}

})